import FileOutlinedIcon from '@ant-design/icons/FileOutlined'
import FileSearchOutlinedIcon from '@ant-design/icons/FileSearchOutlined'

import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Link from '@components/_mui/Link'
import Typography from '@components/_mui/Typography'

import Image from '@shared/components/src/Image'
import useDependencyKey from '@shared/hooks/src/useDependencyKey'
import { isImage, isPdf } from '@shared/utils'
import { Document, Thumbnail } from '@shared/utils/src/pdf'

import Section from '@components/Details/Section'
import FileHover from '@components/FileHover'

export default function RequisitionsSection({ data, mini = false }) {
  const filesAvailable = data.orderFiles?.length > 0 || data.resultFiles?.length > 0 || data.reviewFiles?.length > 0

  return (
    <Section
      mini={mini}
      title="Requisition"
      icon={mini ? null : <FileSearchOutlinedIcon />}
      action={data.requisition ? <Typography variant="h5">{`ID #${data.requisition}`}</Typography> : null}
    >
      {!filesAvailable && <Typography>No files available</Typography>}
      {filesAvailable && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: 1,
            '& .react-pdf__message': { height: 200 },
          }}
        >
          {data.orderFiles?.map((file) => (
            <FileWrap key={file.uid} title="Order">
              <FilePreview file={file} />
            </FileWrap>
          ))}
          {data.resultFiles?.map((file) => (
            <FileWrap key={file.uid} title="Results">
              <FilePreview file={file} />
            </FileWrap>
          ))}
          {data.reviewFiles?.map((file) => (
            <FileWrap key={file.uid} title="Reviews">
              <FilePreview file={file} />
            </FileWrap>
          ))}
        </Box>
      )}
    </Section>
  )
}

RequisitionsSection.Loading = ({ mini = false }) => (
  <Section
    mini={mini}
    title="Requisition"
    icon={mini ? null : <FileSearchOutlinedIcon />}
    action={
      <Typography variant="h5">
        <Skeleton width={100} />
      </Typography>
    }
  >
    <Stack direction="row" spacing={3}>
      <FileWrap title={<Skeleton width={60} />}>
        <FilePreviewLoading />
      </FileWrap>
      <FileWrap title={<Skeleton width={80} />}>
        <FilePreviewLoading />
      </FileWrap>
    </Stack>
  </Section>
)

function FileWrap({ title, children }) {
  return (
    <Stack spacing={1}>
      <Typography variant="h5" sx={{ fontWeight: 'light' }}>
        {title}
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          height: 200,
        }}
      >
        {children}
      </Paper>
    </Stack>
  )
}

function FilePreview({ file }) {
  const { mimetype } = file

  const image = isImage(mimetype)
  const pdf = isPdf(mimetype)

  if (image) {
    return (
      <Link target="_blank" href={file.url}>
        <Image fit="cover" duration={500} src={file.url} errorIcon={<FileOutlinedIcon />} />
        <FileHover />
      </Link>
    )
  }

  if (pdf) {
    return (
      <Link target="_blank" href={file.url}>
        <PdfThumbnail file={file} />
        <FileHover />
      </Link>
    )
  }

  return (
    <Link target="_blank" href={file.url}>
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          textAlign: 'center',
        }}
      >
        File type <br /> not supported
      </Stack>
      <FileHover />
    </Link>
  )
}

function PdfThumbnail({ file }) {
  const key = useDependencyKey(file.url)

  return (
    <Document key={key} file={file.url}>
      <Thumbnail pageNumber={1} renderTextLayer={false} renderAnnotationLayer={false} width={150} />
    </Document>
  )
}

const FilePreviewLoading = () => <Skeleton variant="rounded" width={150} height={200} />
